import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AccountCard from "../components/AccountCard";
import CurrencyInput from "../components/CurrencyInput";
import { useSelected } from "../contexts/SelectedContext";
import useSubscriptionStatus from '../hooks/useSubscriptionStatus';

// Import des hooks et constantes de Thème
import { useTheme } from "../contexts/ThemeContext";
import { useNotification } from '../contexts/NotificationContext';
import { THEMES, THEME_COLORS } from "../components/Constants";

import { processRecurrences } from '../services/recurrenceService';
import { authFetch } from "../utils/api"; 


export default function AccountsPage({ currentUser, persons, accounts, refreshAllData }) {

  // Récupération du thème
  const { currentTheme } = useTheme();
  const { notify } = useNotification();
  const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

  const { selectedPerson, setSelectedPerson, setSelectedAccount } = useSelected();

  const [showPersonForm, setShowPersonForm] = useState(false);
  const [newPersonName, setNewPersonName] = useState("");

  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountName, setAccountName] = useState("");
  const [soldeDebutAnnee, setSoldeDebutAnnee] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingAccount, setEditingAccount] = useState(null);

  const { maxAccounts, isAdmin } = useSubscriptionStatus();
  const canAddAccount = isAdmin || (accounts || []).length < maxAccounts;

  const navigate = useNavigate();

  // ----------------------------------------------------------------------
  // ✅ LOGIQUE D'AJOUT DE PERSONNE (Sécurisée et Nettoyée)
  // ----------------------------------------------------------------------
  const runRecurrenceCheck = useCallback(async () => {
    if (!currentUser) return;
    try {
      const message = await processRecurrences();
      console.log(`✅ Récurrences traitées : ${message}`);
      if (refreshAllData) {
        refreshAllData();
      }
    } catch (error) {
      console.warn(`⚠️ Le processus de récurrence a échoué (cela peut être normal si aucune transaction n'était due) : ${error.message}`);
    }
  }, [currentUser, refreshAllData]);

  useEffect(() => {
      // On ne lance la vérification QUE si on n'est pas en mode assistance
      // et que l'utilisateur est bien connecté
      if (currentUser && !currentUser.adminMode) {
          runRecurrenceCheck();
      }
      // L'ajout de runRecurrenceCheck et currentUser ici règle le warning ESLint
  }, [runRecurrenceCheck, currentUser]);

  // Filtrer les personnes gérées (is_managed = true)
  const safePersons = Array.isArray(persons) ? persons : [];
  const managedPersons = safePersons.filter(p => p.is_managed === true || p.is_managed === 1);

  useEffect(() => {
        // Condition : Si nous avons des personnes disponibles ET qu'aucune personne n'est sélectionnée
        if (!selectedPerson && managedPersons.length > 0) {

            const defaultPerson = managedPersons[0];
            setSelectedPerson(defaultPerson);

            const defaultAccount = (accounts || []).find(acc => acc.personId === defaultPerson.id);
            if (defaultAccount) {
                setSelectedAccount(defaultAccount);
            }
        }
    }, [selectedPerson, managedPersons, setSelectedPerson, accounts, setSelectedAccount]);

  const handleAddPerson = (e) => {
    e.preventDefault();
    
    // 🔑 NETTOYAGE : L'ID utilisateur n'est plus envoyé dans le corps de la requête.
    // Le backend le récupère du JWT.
    const dataToSend = { name: newPersonName, is_managed: true };
    
    // 🔑 SÉCURISATION : Utilisation de authFetch
    authFetch.post("/persons", dataToSend)
      .then(async (res) => {
          // Important: Vérifier res.ok car fetch ne rejette pas sur les erreurs 400/500
          if (!res.ok) {
              const errorData = await res.json().catch(() => ({}));
              throw new Error(errorData.error || "Échec de l'ajout de la personne.");
          }
          return res.json();
      })
      .then((newPersonData) => {
        setNewPersonName("");
        setShowPersonForm(false)
        setSelectedPerson(newPersonData);
        setSelectedAccount(null);

        refreshAllData();
        notify(`Personne "${newPersonData.name}" ajoutée et sélectionnée !`);
      })
      .catch((err) => notify("❌ Erreur ajout person :", err));
  };

  // ----------------------------------------------------------------------
  // ✅ LOGIQUE D'AJOUT/MODIFICATION DE COMPTE (Sécurisée et Nettoyée)
  // ----------------------------------------------------------------------
  const handleAddAccount = (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    const isEditing = !!editingAccount;
    const method = isEditing ? "put" : "post";
    const urlPath = isEditing
      ? `/accounts/${editingAccount.id}` // Chemin relatif
      : `/accounts`;

    const dataToSend = {
      name: accountName,
      soldeDebutAnnee: parseFloat(soldeDebutAnnee) || 0,
      ...(isEditing ? {} : { personId: selectedPerson.id }), 
    };

    // 🔑 SÉCURISATION : Utilisation de authFetch
    authFetch[method](urlPath, dataToSend)
      .then(async (res) => {
        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.error || `Échec de l'opération.`);
        }
        return res.json();
      })
      .then(() => {
        closeAccountForm();
        refreshAllData();
        notify(`Compte ${isEditing ? 'modifié' : 'ajouté'} avec succès !`);
      })
      .catch((err) =>  notify(`❌ Erreur ${isEditing ? 'modification' : 'ajout'} account :`, err.message))
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setAccountName(account.name);
    setSoldeDebutAnnee(String(account.soldeDebutAnnee));
    setShowAccountForm(true);
    setShowPersonForm(false); // Ferme l'autre formulaire
  };

  const closeAccountForm = () => {
    setShowAccountForm(false);
    setEditingAccount(null);
    setAccountName("");
    setSoldeDebutAnnee("");
  };

  const accountsForSelectedPerson = (accounts || []).filter(
      (account) => account.personId === selectedPerson?.id
    );

  const totalSolde = accountsForSelectedPerson.reduce(
    (total, acc) => total + (parseFloat(acc.soldeActuel) || 0),
    0
  );

  const formatEuro = (value) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(value);

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    navigate("/dashboard");
  };

  return (
    <div style={styles.container}>
      <div style={{ ...styles.contentCard, color: themeColors.text }}>
        <h2 style={{ ...styles.title, color: themeColors.secondary }}>Gestion de vos comptes</h2>

        {/* --- SECTION 1 : CHOIX OU AJOUT DE PERSONNE --- */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Personne gérée :</label>
          {managedPersons.length > 0 ? (
            <select
              style={{
                ...styles.select,
                border: `1px solid ${themeColors.secondary}`,
                backgroundColor: themeColors.background,
              }}
              value={selectedPerson?.id || ""}
              onChange={(e) => {
                const newPerson = persons.find((p) => p.id === parseInt(e.target.value));
                setSelectedPerson(newPerson);
                setShowPersonForm(false);
                setShowAccountForm(false);
              }}
            >
              <option value="">-- Sélectionner une personne --</option>
              {managedPersons.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          ) : (
            <p style={{ fontStyle: 'italic', fontSize: '0.9em', color: themeColors.secondary }}>
              Commencez par ajouter une personne (vous-même ou un membre du foyer).
            </p>
          )}
        </div>

        <button
          style={{
            ...styles.actionButton,
            color: themeColors.textbutton,
            backgroundColor: showPersonForm ? themeColors.defaultButton : themeColors.primary,
            marginBottom: selectedPerson ? '20px' : '0px' // Ajustement visuel
          }}
          onClick={() => {
            setShowPersonForm(!showPersonForm);
            setShowAccountForm(false);
          }}
        >
          {showPersonForm ? `Annuler` : `Ajouter une personne`}
        </button>

        {showPersonForm && (
          <form onSubmit={handleAddPerson} style={{
            ...styles.formContainer,
            border: `1px solid ${themeColors.secondary}`,
            backgroundColor: themeColors.formBackground
          }}>
            <input
              style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }}
              type="text"
              placeholder="Ex: Jean, Marie, ou 'Moi'..."
              value={newPersonName}
              onChange={(e) => setNewPersonName(e.target.value)}
              required
            />
            <button style={{ ...styles.button, backgroundColor: themeColors.secondary, width: '100%' }} type="submit">
              Enregistrer la personne
            </button>
          </form>
        )}

        {/* --- SECTION 2 : GESTION DES COMPTES (Visible UNIQUEMENT si une personne est sélectionnée) --- */}
        {selectedPerson ? (
          <>
            <h3 style={{ ...styles.personTitle, borderBottom: `2px solid ${themeColors.inputBorder}` }}>
              Comptes de {selectedPerson.name}
            </h3>
            
            <div style={styles.accountList}>
              {accountsForSelectedPerson.length === 0 ? (
                <div style={styles.infoMessage}>
                  Aucun compte enregistré pour {selectedPerson.name}.
                </div>
              ) : (
                accountsForSelectedPerson.map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account} // account.soldeActuel est déjà le bon !
                    onSelect={handleSelectAccount}
                    onEdit={() => handleEditAccount(account)}
                  />
                ))
              )}
            </div>

            {accountsForSelectedPerson.length > 0 && (
              <div style={{ ...styles.total, borderTop: `1px dashed ${themeColors.inputBorder}` }}>
                Total des soldes : <strong style={{ color: totalSolde >= 0 ? themeColors.success : themeColors.danger }}>{formatEuro(totalSolde)}</strong>
              </div>
            )}

            {/* Bouton d'ajout de compte avec la limite d'abonnement */}
            <button
              style={{
                ...styles.actionButton,
                color: themeColors.textbutton,
                backgroundColor: !canAddAccount ? '#ccc' : (showAccountForm ? themeColors.defaultButton : themeColors.secondary),
                cursor: !canAddAccount ? 'not-allowed' : 'pointer',
                marginTop: '10px'
              }}
              disabled={!canAddAccount}
              onClick={() => {
                if(!canAddAccount) return;
                setShowAccountForm(!showAccountForm);
                setShowPersonForm(false);
              }}
            >
              {showAccountForm ? "Annuler" : (canAddAccount ? "Ajouter un compte" : `Limite atteinte (${maxAccounts} max)`)}
            </button>

            {showAccountForm && (
              <form onSubmit={handleAddAccount} style={{
                  ...styles.formContainer,
                  border: `1px solid ${themeColors.secondary}`,
                  backgroundColor: themeColors.formBackground
              }}
                key={editingAccount ? editingAccount.id : 'new-account'}
              >
                <h3>{editingAccount ? `Modifier le compte: ${editingAccount.name}` : 'Ajouter un nouveau compte'}</h3>
                <input
                  style={{ ...styles.input, border: `1px solid ${themeColors.secondary}`, backgroundColor: themeColors.inputBackground }}
                  type="text"
                  placeholder="Nom du compte"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  required
                />
                <CurrencyInput
                  placeholder="Solde début d'année"
                  value={soldeDebutAnnee}
                  onChange={setSoldeDebutAnnee}
                  // Passe les styles du thème
                  inputStyle={{ ...styles.input, border: `1px solid ${themeColors.secondary}`, backgroundColor: themeColors.inputBackground }}
                />
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={closeAccountForm}
                    // Couleur du bouton Annuler
                    style={{ ...styles.button, backgroundColor: themeColors.defaultButton }}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </button>
                  <button
                    // Couleur du bouton Enregistrer/Ajouter (Succès/Primaire)
                    style={{ ...styles.button, backgroundColor: themeColors.success }}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "En cours..." : (editingAccount ? "Enregistrer" : "Ajouter")}
                  </button>
                </div>
              </form>
            )}
          </>
        ) : (
          /* Message d'attente si aucune personne n'est choisie */
          !showPersonForm && managedPersons.length > 0 && (
            <p style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
              Veuillez sélectionner une personne ci-dessus pour gérer ses comptes.
            </p>
          )
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "calc(100vh - 60px)",
    padding: "40px 20px",
    display: "flex",
    justifyContent: "center",
    alignItems: 'flex-start',
  },
  contentCard: {
    // Background et couleur de texte gérés dynamiquement
    padding: "30px",
    borderRadius: 8,
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: 600,
    backgroundColor: '#fcfcfc',
  },
  title: { textAlign: "center", marginBottom: 30 }, // Couleur gérée dynamiquement
  personTitle: { marginTop: 25, marginBottom: 15, borderBottom: `2px solid #D4D4D4` }, // Bordure gérée dynamiquement
  inputGroup: { marginBottom: 20 },
  label: { display: 'block', marginBottom: '5px', fontWeight: 'bold' },
  select: {
    width: "100%",
    padding: 10,
    fontSize: 16,
    borderRadius: 4,
    // Bordure et fond gérés dynamiquement
    marginTop: 8,
    outline: 'none',
  },
  accountList: {
    display: "flex",
    flexDirection: "column",
    gap: 15,
    marginBottom: 20,
  },
  total: {
    fontSize: 18,
    textAlign: "right",
    marginBottom: 20,
    fontWeight: 'bold',
    paddingTop: '10px',
    // Bordure gérée dynamiquement
  },
  actionButton: { // Style générique pour les boutons Ajouter/Annuler
    color: "#fff",
    padding: 12,
    width: "100%",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    marginBottom: 20,
    fontWeight: 'bold',
    transition: 'background-color 0.2s',
    outline: 'none',
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 20,
    padding: 15,
    // Bordure et fond gérés dynamiquement
    borderRadius: 8,
  },
  input: {
    padding: 10,
    fontSize: 16,
    borderRadius: 4,
    // Bordure et fond gérés dynamiquement
    outline: 'none',
  },
  button: { // Bouton de soumission interne au formulaire (Ajouter/Enregistrer)
    // Background géré dynamiquement
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: 'bold',
    transition: 'background-color 0.2s',
    width: 'auto',
    padding: '10px 20px',
    outline: 'none',
  },
  infoMessage: {
    // Couleurs gérées dynamiquement
    textAlign: 'center',
    fontStyle: 'italic',
    padding: '10px',
    borderRadius: '4px',
    backgroundColor: '#f0f8ff', // J'ai conservé ce bleu clair car il n'est pas dans la palette, mais il pourrait être remplacé par une couleur plus neutre
  }
};